import { includes, isString, map, size, split } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import StyledSystemPropTypes, { propType as StyledSystemPropType } from '@styled-system/prop-types';

import { Box, Flex } from '../grid';
import { H2 } from '../typography/new';
import { H2 as OldH2 } from '../typography';

const HeadingWithLineHighlights = ({
  heading,
  color,
  highlightColor,
  highlightBackgroundColor,
  highlightPadding,
  borderRadius,
  overrideHeadingHighlighting,
  pb,
  mt,
  mb,
  headingLevel,
  shouldWrapLongLines,
  lineHeight,
  textAlign,
  justifyContent,
  ...rest
}) => {
  if (!heading) {
    return null;
  }

  if (!isString(heading)) {
    return (
      <OldH2 mt={mt} mb={mb} pb={pb ?? 1} {...rest}>
        {heading}
      </OldH2>
    );
  }

  const CurrentHeadingLevel = isString(headingLevel) ? H2 : headingLevel;

  // Dynamic styling based on heading level
  const headingStylesMap = {
    OversizedH1: {
      borderRadius: { xs: '10.5px', lg: '15px' },
      padding: {
        pt: { xs: '14.5px', lg: '17.5px' },
        pb: { xs: '0.5px', lg: '0.5px' },
        px: { xs: '11px', lg: '16px' },
      },
    },
    H1: {
      borderRadius: { xs: '7.5px', lg: '14.5px' },
      padding: {
        pt: { xs: '10.5px', lg: '17.5px' },
        pb: { xs: '0.5px', lg: '0.5px' },
        px: { xs: '8px', lg: '15px' },
      },
    },
    H2: {
      borderRadius: { xs: '7px', lg: '12.25px' },
      padding: {
        pt: { xs: '10.5px', lg: '15.5px' },
        pb: { xs: '0.5px', lg: '0.5px' },
        px: { xs: '8px', lg: '13px' },
      },
    },
    H2Mobile: {
      borderRadius: { xs: '7px' },
      padding: {
        pt: { xs: '10.5px' },
        pb: { xs: '0.5px' },
        px: { xs: '8px' },
      },
    },
    H3: {
      borderRadius: { xs: '6.25px', lg: '10px' },
      padding: {
        pt: { xs: '8px', lg: '14.5px' },
        pb: { xs: '1px', lg: '0.5px' },
        px: { xs: '6px', lg: '10px' },
      },
    },
    H4: {
      borderRadius: { xs: '5.5px', lg: '7px' },
      padding: {
        pt: { xs: '10px', lg: '10.5px' },
        pb: { xs: '1px', lg: '0.5px' },
        px: { xs: '6px', lg: '7px' },
      },
    },
  };

  const getHeadingStyles = HeadingComponent => {
    const headingName = HeadingComponent?.displayName || HeadingComponent?.name || 'H2';
    return headingStylesMap[headingName] || headingStylesMap.H2;
  };

  const dynamicStyles = getHeadingStyles(CurrentHeadingLevel);

  const headingAsLines = split(heading, '\n');

  // If more than 1 line, don't wrap lines, so the CMS user can control where line breaks are placed and so, which lines are highlighted.
  // If only 1 line we need to support auto line wrapping, for compatibility with existing CMS entries.
  const autoWrapLongLines = size(headingAsLines) === 1 || shouldWrapLongLines;

  const alwaysHighlightFirstLine = includes(overrideHeadingHighlighting, 'alwaysHighlightFirstLine');
  const alwaysHighlightLastLine = includes(overrideHeadingHighlighting, 'alwaysHighlightLastLine');
  const highlightAllLines = includes(overrideHeadingHighlighting, 'highlightAllLines');
  return (
    <Flex justifyContent={justifyContent} width="100%">
      <CurrentHeadingLevel
        whiteSpace={autoWrapLongLines ? undefined : 'nowrap'}
        mt={mt}
        mb={mb}
        pb={pb ?? 1}
        textAlign={textAlign}
        color={color}
        width="100%"
        shouldWrapLongLines={shouldWrapLongLines}
        position="relative"
        {...rest}
      >
        {map(headingAsLines, (line, index) => {
          const isFirstLine = index === 0;
          const isLastLine = index === size(headingAsLines) - 1;
          const isFirstOrLastLine = index === 0 || isLastLine;
          let shouldHighlight = !isFirstOrLastLine;
          if (isFirstLine && alwaysHighlightFirstLine) {
            shouldHighlight = true;
          }
          if (highlightAllLines || (isLastLine && alwaysHighlightLastLine)) {
            shouldHighlight = true;
          }

          const backgroundColor = shouldHighlight ? highlightBackgroundColor : undefined;

          // Calculate margin bottom for line spacing when using display:block
          const calculateLineMargin = lh =>
            typeof lh === 'object'
              ? Object.fromEntries(Object.entries(lh).map(([key, val]) => [key, `${(val - 1) * 0.35}em`]))
              : `${(lh - 1) * 0.35}em`;
          const marginBottom = !autoWrapLongLines && !isLastLine ? calculateLineMargin(lineHeight) : undefined;

          return (
            <Box
              key={index}
              as="span"
              backgroundColor={backgroundColor}
              mx="auto"
              width="fit-content"
              padding={
                highlightPadding || {
                  xs: `${dynamicStyles.padding.pt.xs} ${dynamicStyles.padding.px.xs} ${dynamicStyles.padding.pb.xs}`,
                  lg: `${dynamicStyles.padding.pt.lg} ${dynamicStyles.padding.px.lg} ${dynamicStyles.padding.pb.lg}`,
                }
              }
              lineHeight={!autoWrapLongLines ? 1 : lineHeight}
              display={!autoWrapLongLines && 'block'}
              mb={marginBottom}
              boxDecorationBreak={autoWrapLongLines && 'clone'}
              color={shouldHighlight ? highlightColor : undefined}
              borderRadius={borderRadius || dynamicStyles.borderRadius}
              maxWidth="100%"
            >
              {line}
              {shouldWrapLongLines && <br />}
            </Box>
          );
        })}
      </CurrentHeadingLevel>
    </Flex>
  );
};

HeadingWithLineHighlights.propTypes = {
  heading: PropTypes.string.isRequired,
  highlightBackgroundColor: StyledSystemPropTypes.color.backgroundColor,
  highlightPadding: StyledSystemPropTypes.space.padding,
  borderRadius: StyledSystemPropTypes.border.border,
  color: StyledSystemPropTypes.color.color,
  highlightColor: StyledSystemPropTypes.color.color,
  overrideHeadingHighlighting: PropTypes.arrayOf(PropTypes.string),
  m: StyledSystemPropType,
  mb: StyledSystemPropType,
  ml: StyledSystemPropType,
  mr: StyledSystemPropType,
  mt: StyledSystemPropType,
  mx: StyledSystemPropType,
  my: StyledSystemPropType,
  p: StyledSystemPropType,
  pb: StyledSystemPropType,
  pl: StyledSystemPropType,
  pr: StyledSystemPropType,
  pt: StyledSystemPropType,
  px: StyledSystemPropType,
  py: StyledSystemPropType,
  headingLevel: PropTypes.node,
  textAlign: PropTypes.string,
  shouldWrapLongLines: PropTypes.bool,
  lineHeight: StyledSystemPropTypes.typography.lineHeight,
  justifyContent: PropTypes.string,
};

HeadingWithLineHighlights.defaultProps = {
  highlightBackgroundColor: null,
  highlightPadding: null,
  borderRadius: null,
  color: null,
  highlightColor: null,
  overrideHeadingHighlighting: null,
  headingLevel: H2,
  m: null,
  mb: null,
  ml: null,
  mr: null,
  mt: null,
  mx: null,
  my: null,
  p: null,
  pb: null,
  pl: null,
  pr: null,
  pt: null,
  px: null,
  py: null,
  shouldWrapLongLines: null,
  lineHeight: { xs: 1.35, lg: 1.31 },
  textAlign: 'center',
  justifyContent: 'center',
};

export { HeadingWithLineHighlights };
